// The navigation bar and its glass furniture: round icon buttons, a pill
// group, text buttons. Content scrolls under it on a phone.
import type { ButtonHTMLAttributes, HTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from './Icon';
import { cx } from './cx';

export interface TopbarProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  title?: string;
  /** Left-aligned, for a screen whose title leads. */
  lead?: boolean;
  /** The heading level this screen's title takes. */
  as?: 'h1' | 'h2' | 'div';
  leading?: ReactNode;
  trailing?: ReactNode;
}

export function Topbar({ title, lead, as: Tag = 'h1', leading, trailing, className, ...rest }: TopbarProps) {
  return (
    <header className={cx('topbar', className)} {...rest}>
      <div className="topbar-side">{leading}</div>
      {title && <Tag className={cx('topbar-title', lead && 'lead')}>{title}</Tag>}
      <div className="topbar-side end">{trailing}</div>
    </header>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  /** Its name, since the icon is all there is. */
  label: string;
  tone?: 'glass' | 'honey' | 'ink';
}

export function IconButton({ icon, label, tone = 'glass', className, type = 'button', ...rest }: IconButtonProps) {
  return (
    <button type={type} className={cx('iconbtn', tone !== 'glass' && tone, className)} aria-label={label} {...rest}>
      <Icon name={icon} />
    </button>
  );
}

/** Several icon buttons in one glass pill. */
export function PillGroup({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('pillgroup', className)}>{children}</div>;
}

export interface TextButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> { bold?: boolean }

export function TextButton({ bold, className, type = 'button', ...rest }: TextButtonProps) {
  return <button type={type} className={cx('textbtn', bold && 'bold', className)} {...rest} />;
}

export function BackButton({ label = 'Back', ...rest }: Omit<IconButtonProps, 'icon' | 'label'> & { label?: string }) {
  return <IconButton icon="back" label={label} tone="ink" {...rest} />;
}
